import type { JSX } from "react";
import type {
  LayoutBreakpoint,
  LayoutSectionAlign,
  LayoutSectionChild,
  LayoutSectionComponent,
  LayoutSectionGap,
  LayoutSpan,
} from "../../types";
import { getSharedComponentRegistry } from "../sharedRegistry";

/**
 * Responsive 12-column layout section: any number of child widgets, each with
 * a per-breakpoint width (span 1–12) and visibility. A value set at a
 * breakpoint cascades DOWN to the smaller breakpoints until one of them
 * carries its own explicit value — the resolution lives in
 * {@link resolveSpan}/{@link resolveSpanSource}, which the admin editor
 * imports too, so what the editor labels « hérité » is exactly what renders.
 *
 * Children render through the shared injected registry (see
 * `../sharedRegistry` for the anti-cycle rationale), so any registered
 * component type can sit in a column. Tolerant by design: an unknown child
 * type renders nothing, malformed spans normalise to full width, and a nested
 * LayoutSection child is refused (anti-recursion guard).
 *
 * Every Tailwind class below is a full static string (enumerated span and
 * visibility maps) so the JIT scanner can see them — no breakpoint
 * interpolation.
 */

const DEFAULT_SPAN: LayoutSpan = 12;

/** Normalises an authored span to a supported value (12 on anything invalid). */
function normalizeSpan(value: unknown): LayoutSpan | undefined {
  if (typeof value !== "number" || !Number.isInteger(value)) return undefined;
  return value >= 1 && value <= 12 ? (value as LayoutSpan) : undefined;
}

/**
 * Resolves the effective span of a child at a breakpoint, applying the
 * downward cascade: base ← md ← lg ← default (12).
 */
export function resolveSpan(span: LayoutSectionChild["span"], bp: LayoutBreakpoint): LayoutSpan {
  return resolveSpanSource(span, bp).value;
}

/**
 * Like {@link resolveSpan} but also reports WHERE the value came from —
 * "explicit" (set on this breakpoint), the larger breakpoint it cascaded
 * from, or "default" (nothing set anywhere). The admin editor uses the
 * source to label inherited width chips.
 */
export function resolveSpanSource(
  span: LayoutSectionChild["span"],
  bp: LayoutBreakpoint,
): { value: LayoutSpan; source: "explicit" | LayoutBreakpoint | "default" } {
  const base = normalizeSpan(span?.base);
  const md = normalizeSpan(span?.md);
  const lg = normalizeSpan(span?.lg);
  if (bp === "base") {
    if (base !== undefined) return { value: base, source: "explicit" };
    if (md !== undefined) return { value: md, source: "md" };
    if (lg !== undefined) return { value: lg, source: "lg" };
    return { value: DEFAULT_SPAN, source: "default" };
  }
  if (bp === "md") {
    if (md !== undefined) return { value: md, source: "explicit" };
    if (lg !== undefined) return { value: lg, source: "lg" };
    return { value: DEFAULT_SPAN, source: "default" };
  }
  if (lg !== undefined) return { value: lg, source: "explicit" };
  return { value: DEFAULT_SPAN, source: "default" };
}

/**
 * Resolves the effective visibility of a child at a breakpoint, with the same
 * downward cascade as {@link resolveSpan}; default visible.
 */
export function resolveVisible(visible: LayoutSectionChild["visible"], bp: LayoutBreakpoint): boolean {
  const base = typeof visible?.base === "boolean" ? visible.base : undefined;
  const md = typeof visible?.md === "boolean" ? visible.md : undefined;
  const lg = typeof visible?.lg === "boolean" ? visible.lg : undefined;
  if (bp === "base") return base ?? md ?? lg ?? true;
  if (bp === "md") return md ?? lg ?? true;
  return lg ?? true;
}

/** Fully enumerated (statically analyzable) span classes, per breakpoint. */
const BASE_SPAN: Record<LayoutSpan, string> = {
  1: "col-span-1",
  2: "col-span-2",
  3: "col-span-3",
  4: "col-span-4",
  5: "col-span-5",
  6: "col-span-6",
  7: "col-span-7",
  8: "col-span-8",
  9: "col-span-9",
  10: "col-span-10",
  11: "col-span-11",
  12: "col-span-12",
};

const MD_SPAN: Record<LayoutSpan, string> = {
  1: "md:col-span-1",
  2: "md:col-span-2",
  3: "md:col-span-3",
  4: "md:col-span-4",
  5: "md:col-span-5",
  6: "md:col-span-6",
  7: "md:col-span-7",
  8: "md:col-span-8",
  9: "md:col-span-9",
  10: "md:col-span-10",
  11: "md:col-span-11",
  12: "md:col-span-12",
};

const LG_SPAN: Record<LayoutSpan, string> = {
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
  4: "lg:col-span-4",
  5: "lg:col-span-5",
  6: "lg:col-span-6",
  7: "lg:col-span-7",
  8: "lg:col-span-8",
  9: "lg:col-span-9",
  10: "lg:col-span-10",
  11: "lg:col-span-11",
  12: "lg:col-span-12",
};

/**
 * Visibility classes per resolved (base, md, lg) boolean triple, keyed
 * "bml" with 1=visible. "000" never reaches the map — the child is simply
 * not rendered.
 */
const VISIBILITY_CLASSES: Record<string, string> = {
  "111": "",
  "011": "hidden md:block",
  "001": "hidden lg:block",
  "101": "block md:hidden lg:block",
  "110": "lg:hidden",
  "100": "md:hidden",
  "010": "hidden md:block lg:hidden",
};

const GAP_CLASSES: Record<LayoutSectionGap, string> = {
  none: "gap-0",
  sm: "gap-3 md:gap-4",
  md: "gap-6",
  lg: "gap-8 md:gap-10",
};

const ALIGN_CLASSES: Record<LayoutSectionAlign, string> = {
  start: "items-start",
  center: "items-center",
  stretch: "items-stretch",
};

/** Normalises an authored gap ("md" on anything invalid). */
function resolveGap(gap: unknown): LayoutSectionGap {
  return typeof gap === "string" && gap in GAP_CLASSES ? (gap as LayoutSectionGap) : "md";
}

/** Normalises an authored alignment ("start" on anything invalid). */
function resolveAlign(align: unknown): LayoutSectionAlign {
  return typeof align === "string" && align in ALIGN_CLASSES ? (align as LayoutSectionAlign) : "start";
}

/**
 * Renders one column's child through the shared registry. Returns null for an
 * absent/malformed child, an unregistered type, a registry not yet injected
 * (leaf unit tests), or a nested LayoutSection — nesting the layout inside
 * itself is refused so a mis-authored payload can never recurse.
 */
function renderChild(child: LayoutSectionChild | undefined): JSX.Element | null {
  if (!child || typeof child.componentType !== "string") return null;
  if (child.componentType === "LayoutSection") {
    // Anti-recursion guard: a layout cannot host another layout.
    return null;
  }
  const renderer = getSharedComponentRegistry()?.[child.componentType];
  return renderer ? renderer(child) : null;
}

/** Renderer for the responsive 12-column layout section. */
export function LayoutSectionRenderer({ component }: { component: LayoutSectionComponent }) {
  const content = component.content ?? ({} as LayoutSectionComponent["content"]);
  const children = Array.isArray(content.children) ? content.children : [];

  const rendered = children
    .map((child, index) => {
      const node = renderChild(child);
      if (!node) return null;
      const visibleBase = resolveVisible(child.visible, "base");
      const visibleMd = resolveVisible(child.visible, "md");
      const visibleLg = resolveVisible(child.visible, "lg");
      const visibilityKey = `${visibleBase ? 1 : 0}${visibleMd ? 1 : 0}${visibleLg ? 1 : 0}`;
      // Hidden everywhere → not rendered at all.
      if (visibilityKey === "000") return null;
      const spanBase = resolveSpan(child.span, "base");
      const spanMd = resolveSpan(child.span, "md");
      const spanLg = resolveSpan(child.span, "lg");
      const visibility = VISIBILITY_CLASSES[visibilityKey];
      const className = [
        "min-w-0",
        BASE_SPAN[spanBase],
        MD_SPAN[spanMd],
        LG_SPAN[spanLg],
        visibility,
      ]
        .filter(Boolean)
        .join(" ");
      return (
        <div key={child.key ?? index} className={className} data-layout-column={index}>
          {node}
        </div>
      );
    })
    .filter((node): node is JSX.Element => node !== null);

  // Nothing renderable → no empty grid shell in the DOM.
  if (rendered.length === 0) return null;

  const gapClass = GAP_CLASSES[resolveGap(content.gap)];
  const alignClass = ALIGN_CLASSES[resolveAlign(content.align)];
  return (
    <section data-layout-section>
      <div className={`grid grid-cols-12 ${gapClass} ${alignClass}`}>{rendered}</div>
    </section>
  );
}
